import React, { useEffect, useRef, useState } from 'react';
import {
  TeButton,
  TeContainer,
  TeContainerSlim,
  TeContextMenu,
  TeContextMenuControl,
  TeContextMenuItem,
  TeImage,
  TeText,
} from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';

import { ClientBody } from '../../../api/generated/pap';

type Props = {
  application: ClientBody;
  disabled?: boolean;
  onEdit?: (app: ClientBody) => void;
  onDelete?: (app: ClientBody) => void;
};

const CustomApplicationCard = ({ application, disabled, onEdit, onDelete }: Props) => {
  const { t } = useTranslation('settings');

  const [iconSrc, setIconSrc] = useState<string>(application.iconURL ?? '');
  const iconObjectUrlRef = useRef<string | null>(null);

  const parseDateTime = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);

    if (isNaN(date.getTime())) return '-';

    // date formatted according to the user's locale (ordering and separators may vary)
    const localizedDate = date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });

    // time always shown as HH:MM:SS (local time)
    const pad = (n: number) => String(n).padStart(2, '0');
    const time = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;

    return `${localizedDate} - ${time}`;
  };

  const properties = [
    {
      key: 'homepageURL',
      label: t('customApplications.uriInput'),
      value: application.homepageURL ?? '-',
    },
    {
      key: 'iconURL',
      label: t('customApplications.iconUriInput'),
      value: application.iconURL ?? '-',
    },
    {
      key: 'clientId',
      label: t('customApplications.clientIdInput'),
      value: application.clientId ?? '-',
    },
    {
      key: 'OAuthConfig',
      label: t('customApplications.oauthConfigurationInput'),
      value: application.OAuthConfig ? JSON.stringify(application.OAuthConfig, null, 2) : '-',
    },
    {
      key: 'addedAt',
      label: t('customApplications.addedAt'),
      value: parseDateTime(String(application.createdAt)),
    },
    {
      key: 'lastUpdated',
      label: t('customApplications.lastUpdated'),
      value: parseDateTime(String(application.modifiedAt)),
    },
  ];

  const renderProperties = () => {
    return properties.map((property, index) => {
      const rowBg = index % 2 === 0 ? 'background.tertiary' : 'background.primary'; // alternate row color
      return (
        <React.Fragment key={property.key}>
          <TeContainerSlim fill padding={[8, 16]} backgroundColor={rowBg}>
            <TeText size={12} weight={400} color="text.primary">
              {property.label}
            </TeText>
          </TeContainerSlim>
          <TeContainerSlim fill padding={[8, 16]} backgroundColor={rowBg}>
            <TeText size={14} weight={500}>
              {property.value}
            </TeText>
          </TeContainerSlim>
        </React.Fragment>
      );
    });
  };

  // needed to handle icon URL updates and cache busting
  // (e.g., when the icon is changed but the URL remains the same)
  useEffect(() => {
    let cancelled = false;

    const url = application.iconURL;
    if (!url) {
      // cleanup any previous object URL
      if (iconObjectUrlRef.current) {
        URL.revokeObjectURL(iconObjectUrlRef.current);
        iconObjectUrlRef.current = null;
      }
      setIconSrc('');
      return;
    }

    // try fetching the image directly (bypass cache) and use an object URL
    fetch(url, { cache: 'no-store' })
      .then((res) => {
        if (!res.ok) throw new Error('Image fetch failed');
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        // revoke previous
        if (iconObjectUrlRef.current) {
          URL.revokeObjectURL(iconObjectUrlRef.current);
        }
        const objUrl = URL.createObjectURL(blob);
        iconObjectUrlRef.current = objUrl;
        setIconSrc(objUrl);
      })
      .catch(() => {
        if (!cancelled) {
          // fallback to the original URL if fetch fails
          setIconSrc(url);
        }
      });

    return () => {
      cancelled = true;
      if (iconObjectUrlRef.current) {
        URL.revokeObjectURL(iconObjectUrlRef.current);
        iconObjectUrlRef.current = null;
      }
    };
  }, [application.iconURL, application.modifiedAt]);

  return (
    <TeContainer
      column
      backgroundColor="background.secondary"
      borderRadius={8}
      grow
      borderColor="border.secondary"
    >
      <TeContainerSlim fill column>
        {/* HEADER */}
        <TeContainerSlim gap="xl" justifyContent="space-between" alignItems="center" fill>
          <TeContainerSlim gap="m" justifyContent="flex-start">
            <TeImage
              borderRadius={12}
              imageSource={iconSrc || application.iconURL || ''}
              height={48}
              width={48}
            />
            <TeContainerSlim column gap="xs">
              <TeText size={16} weight={700}>
                {application.name}
              </TeText>
              <TeText color="text.secondary" size={14}>
                {application.description}
              </TeText>
            </TeContainerSlim>
          </TeContainerSlim>

          <TeContextMenuControl contextId={`custom-app-info-${application.id}`}>
            <TeButton
              size="s"
              postfixIcon="more_vert"
              disabled={disabled}
              blockInteraction={disabled}
              backgroundColor="background.secondary"
              iconColor="text.primary"
            />
          </TeContextMenuControl>
          <TeContextMenu
            placement="bottomLeft"
            contextId={`custom-app-info-${application.id}`}
            size="s"
          >
            <TeContextMenuItem
              text={t('customApplications.edit')}
              teclick={() => onEdit?.(application)}
            />
            <TeContextMenuItem
              text={t('customApplications.delete')}
              textColor="red"
              teclick={() => onDelete?.(application)}
            />
          </TeContextMenu>
        </TeContainerSlim>

        {/* CONTENT */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'max-content 1fr',
            alignItems: 'stretch',
            marginTop: 16,
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          {renderProperties()}
        </div>
      </TeContainerSlim>
    </TeContainer>
  );
};

export default CustomApplicationCard;
